import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useShippingSettings } from '../hooks/useShippingSettings';
import { useEcommerceTracking } from '../hooks/useAnalytics';
import PaymentMethodSelector from '../components/checkout/PaymentMethodSelector';
import { 
  ShoppingBagIcon,
  MapPinIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import axios from '../utils/axios';
import toast from 'react-hot-toast';

const CheckoutPage = () => {
  const { user } = useAuth();
  const { cartItems, getTotalPrice, getSubtotal, clearCart } = useCart();
  const { calculateShipping, calculateTax, settings } = useShippingSettings();
  const { trackCheckoutBegin, trackPurchaseComplete } = useEcommerceTracking();
  const navigate = useNavigate();
  
  // Redirect B2B users to fast checkout
  useEffect(() => {
    if (user?.role === 'b2b') {
      navigate('/fast-checkout', { replace: true });
      return;
    }
  }, [user, navigate]);

  // Track begin_checkout when component mounts
  useEffect(() => {
    if (cartItems && cartItems.length > 0) {
      const total = getTotalPrice();
      trackCheckoutBegin(cartItems, total);
    }
  }, []); // Empty dependency array - only run once on mount
  
  const [currentStep, setCurrentStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [orderDetails, setOrderDetails] = useState({
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: '',
    city: '',
    postalCode: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderCreated, setOrderCreated] = useState(false);

  // Helper function to get payment method labels
  const getPaymentMethodLabel = (method) => {
    const methods = {
      'cash_on_delivery': 'מזומן בזמן האספקה',
      'credit_card': 'כרטיס אשראי',
      'bank_transfer': 'העברה בנקאית',
      'check': 'צ\'ק',
      'company_credit': 'אשראי חברה',
      'installments': 'תשלומים',
      'wire_transfer': 'העברה בינלאומית',
      'cash': 'מזומן'
    };
    return methods[method] || method;
  };

  // Redirect if cart is empty (but not if we just created an order)
  useEffect(() => {
    if (cartItems && cartItems.length === 0 && !orderCreated) {
      navigate('/cart');
    }
  }, [cartItems, navigate, orderCreated]);

  // Loading state while cart loads
  if (cartItems === undefined) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">טוען עגלת קנייה...</p>
        </div>
      </div>
    );
  }

  // If cart is empty, redirect to cart page (but not if we just created an order)
  if (cartItems && cartItems.length === 0 && !orderCreated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">העגלה ריקה</h2>
          <p className="text-gray-600 mb-6">אין מוצרים בעגלת הקנייה שלך</p>
          <button 
            onClick={() => navigate('/cart')}
            className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600"
          >
            חזור לעגלה
          </button>
        </div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOrderDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmitOrder = async () => {
    if (!paymentMethod) {
      toast.error('אנא בחר אמצעי תשלום');
      return;
    }

    const requiredFields = ['firstName', 'lastName', 'phone', 'city'];
    const missingFields = requiredFields.filter(field => !orderDetails[field]?.trim());
    if (missingFields.length > 0) {
      toast.error('אנא מלא את כל השדות הנדרשים');
      return;
    }

    setIsSubmitting(true);
    try {
      const subtotalVal = getSubtotal() || 0;
      const taxVal = calculateTax(subtotalVal);
      const shippingVal = calculateShipping(subtotalVal);
      const totalVal = settings.taxIncluded ? subtotalVal + shippingVal : subtotalVal + taxVal + shippingVal;

      const orderData = {
        items: (cartItems || []).map(item => ({
          product_id: item.product?.id,
          quantity: item.quantity,
          unit_price: item.product?.price || item.product?.price_b2c
        })),
        subtotal: subtotalVal,
        total_amount: totalVal,
        payment_method: paymentMethod,
        order_type: user?.role === 'b2b' ? 'b2b' : 'b2c',
        notes: orderDetails.notes,
        customer_details: {
          first_name: orderDetails.firstName,
          last_name: orderDetails.lastName,
          email: orderDetails.email,
          phone: orderDetails.phone,
          address: orderDetails.address,
          city: orderDetails.city,
          postal_code: orderDetails.postalCode
        }
      };

      const response = await axios.post('/api/orders', orderData);
      
      if (response.data && response.data.order) {
        const order = response.data.order;
        const orderId = order.id || order.order_id;
        
        if (!orderId) {
          toast.error('ההזמנה נוצרה אבל לא התקבל מזהה הזמנה. אנא בדוק את ההזמנות שלך.');
          window.location.href = '/account/order-history';
          return;
        }
        
        // IMPORTANT: Mark as created BEFORE clearing cart to prevent redirect
        setOrderCreated(true);
        
        // Track purchase completion for analytics (non-blocking)
        try {
          trackPurchaseComplete(order);
        } catch (trackError) {
          console.error('Error tracking purchase:', trackError);
        }
        
        // Show success message
        toast.success('ההזמנה נוצרה בהצלחה! מעביר לפרטי ההזמנה...', {
          duration: 2000
        });
        
        // Navigate IMMEDIATELY before clearing cart
        const orderUrl = `/orders/${orderId}`;
        
        // Force navigation with full URL
        const fullUrl = window.location.origin + orderUrl;
        
        // Use replace to prevent back button issues
        window.location.replace(fullUrl);
        
        // Clear cart AFTER navigation is initiated (it will complete even after redirect)
        setTimeout(() => {
        }, 100);
        
      } else {
        toast.error('ההזמנה נוצרה אבל לא התקבלו פרטים. אנא בדוק את ההזמנות שלך.');
        window.location.href = '/account/order-history';
      }
    } catch (error) {
      
      // Check if it's a product not found error
      if (error.response?.data?.message?.includes('Product not found')) {
        alert('אחד המוצרים בעגלה אינו זמין יותר. העגלה תנוקה ותוכל להוסיף מוצרים חדשים.');
        clearCart();
        navigate('/products');
      } else {
        alert('שגיאה ביצירת ההזמנה. אנא נסה שוב.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-safe" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header - Mobile First */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">השלמת הזמנה</h1>
          <p className="text-sm sm:text-base text-gray-600">מלא את הפרטים להשלמת ההזמנה</p>
        </div>

        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {/* Main Content - Mobile First */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6 order-2 lg:order-1">
            
            {/* Customer Details - Mobile Optimized */}
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <MapPinIcon className="h-5 w-5 sm:h-6 sm:w-6 ml-2" />
                פרטי הזמנה
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    שם פרטי *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={orderDetails.firstName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    שם משפחה *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={orderDetails.lastName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    אימייל *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={orderDetails.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    טלפון *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={orderDetails.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div className="sm:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    כתובת *
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={orderDetails.address}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    עיר *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={orderDetails.city}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    מיקוד
                  </label>
                  <input
                    type="text"
                    name="postalCode"
                    value={orderDetails.postalCode}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div className="sm:col-span-2">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                    הערות להזמנה
                  </label>
                  <textarea
                    name="notes"
                    value={orderDetails.notes}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2.5 sm:py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="הערות מיוחדות להזמנה..."
                  />
                </div>
              </div>
            </div>

            {/* Payment Method Selection - Mobile Optimized */}
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <CreditCardIcon className="h-5 w-5 sm:h-6 sm:w-6 ml-2" />
                אמצעי תשלום
              </h2>
              
              <div>
                <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">בחר אמצעי תשלום:</p>
                <PaymentMethodSelector
                  selectedMethod={paymentMethod}
                  onMethodChange={setPaymentMethod}
                  userRole={user?.role || 'b2c'}
                />
              </div>
              
              {paymentMethod && (
                <div className="mt-3 sm:mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs sm:text-sm text-blue-800">
                    ✓ נבחר: {getPaymentMethodLabel(paymentMethod)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Order Summary - Mobile First: Show at top on mobile, sidebar on desktop */}
          <div className="lg:col-span-1 order-1 lg:order-2">
            <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 lg:sticky lg:top-8">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <ShoppingBagIcon className="h-5 w-5 sm:h-6 sm:w-6 ml-2" />
                סיכום הזמנה
              </h2>
              
              <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                {cartItems && cartItems.map((item, index) => (
                  <div key={item.id || item.product?.id || index} className="flex justify-between items-center py-2 border-b border-gray-200 gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-gray-900 line-clamp-1">{item.product?.name || 'מוצר'}</p>
                      <p className="text-xs text-gray-500">כמות: {item.quantity}</p>
                    </div>
                    <p className="text-xs sm:text-sm font-medium text-gray-900 flex-shrink-0">
                      ₪{((item.product?.price_b2c || item.product?.price || 0) * item.quantity).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
              
              <div className="space-y-1.5 sm:space-y-2 mb-4 sm:mb-6">
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-gray-600">סכום ביניים:</span>
                  <span className="text-gray-900">₪{(getSubtotal() || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-gray-600">מע"מ ({settings.taxRate}%):</span>
                  <span className="text-gray-900">₪{calculateTax(getSubtotal() || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xs sm:text-sm">
                  <span className="text-gray-600">משלוח:</span>
                  <span className="text-gray-900">
                    {calculateShipping(getSubtotal() || 0) === 0 ? 'חינם' : `₪${calculateShipping(getSubtotal() || 0).toFixed(2)}`}
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-2">
                  <div className="flex justify-between text-base sm:text-lg font-semibold">
                    <span className="text-gray-900">סה"כ לתשלום:</span>
                    <span className="text-gray-900">
                      ₪{(() => {
                        const subtotal = getSubtotal() || 0;
                        const tax = calculateTax(subtotal);
                        const shipping = calculateShipping(subtotal);
                        // If tax is included in prices, don't add it again
                        const total = settings.taxIncluded ? 
                          subtotal + shipping : 
                          subtotal + tax + shipping;
                        return total.toFixed(2);
                      })()}
                    </span>
                  </div>
                </div>
              </div>
              
              <button
                onClick={handleSubmitOrder}
                disabled={isSubmitting || !paymentMethod}
                className={`w-full py-3 sm:py-3.5 px-4 rounded-lg font-medium text-sm sm:text-base transition-colors touch-manipulation ${
                  isSubmitting || !paymentMethod
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    מעבד הזמנה...
                  </span>
                ) : (
                  'אישור והזמנה 🛒'
                )}
              </button>
              
              <p className="text-xs text-gray-500 text-center mt-2 sm:mt-3">
                לחיצה על "אישור והזמנה" מהווה הסכמה לתנאי השימוש
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
